<template>
    <div>
        <Transition name="fade" @before-enter="beforeEnter" @enter="enter" @leave="leave" class="overflow-hidden">
            <div v-if="hasSpecialPrice" class="mb-2">
                <UiComponentTypography variant="subtitle2"> قیمت ویژه شما: </UiComponentTypography>
            </div>
        </Transition>
        <WidgetsProductPrice size="2xl" :price="finalPrices.price" :discount="finalPrices.discounted_price" />
    </div>
</template>

<script lang="ts" setup>
import type { IPrices, ISpecialPrice } from "~/libs/types/api/product";

const props = defineProps({
    activePrice: Object as PropType<IPrices>,
});

const route = useRoute();
const { isAuthenticated } = useSanctumAuth();

const hasSpecialPrice = ref(false);

const {
    data: specialPrices,
    status,
    error,
    refresh,
} = useAsyncData<ISpecialPrice[]>(
    `${route.params.slug}-special-prices`,
    () =>
        fetchData("/products/get/special-prices", {
            method: "POST",
            useSanctum: true,
            body: {
                product_slug: route.params.slug,
            },
        }),
    {
        server: false,
        immediate: isAuthenticated.value,
        getCachedData(key, nuxtApp) {
            return nuxtApp.payload.data[key] || nuxtApp.static.data[key];
        },
    }
);

const finalPrices = computed(() => {
    if (specialPrices.value && specialPrices.value.length) {
        const tmp = useFind(specialPrices.value, ["parent.uuid", props.activePrice?.uuid]);
        if (tmp) {
            hasSpecialPrice.value = true;
            return { price: tmp.price.price ?? 0, discounted_price: tmp.price.discounted_price };
        }
    } else if (props.activePrice) {
        const calculatedPrice = calculatePrice(props.activePrice);
        if (calculatedPrice.special) {
            hasSpecialPrice.value = true;
        }
        return {
            price: calculatedPrice.price ?? 0,
            discounted_price: calculatedPrice.discounted_price,
        };
    }

    return { price: 0, discounted_price: 0 };
});

// animate
// Define a reactive reference for visibility
const isVisible = ref<boolean>(false);

// Function to toggle visibility
const toggle = () => {
    isVisible.value = !isVisible.value;
};

// Transition lifecycle hooks
const beforeEnter = (el: Element) => {
    (el as HTMLElement).style.height = "0"; // Type assertion to HTMLElement
};

const enter = (el: Element, done: () => void) => {
    const fullHeight = (el as HTMLElement).scrollHeight + "px"; // Type assertion to HTMLElement
    (el as HTMLElement).style.height = fullHeight;

    // Listen for the end of the transition
    el.addEventListener("transitionend", done);
};

const leave = (el: Element, done: () => void) => {
    (el as HTMLElement).style.height = "0"; // Type assertion to HTMLElement

    // Listen for the end of the transition
    el.addEventListener("transitionend", done);
};
</script>

<style>
.fade-enter-active,
.fade-leave-active {
    transition: height 0.3s ease;
}
</style>
